import React from 'react';
import { useInView } from 'react-cool-inview';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  Button, Icon, Image, Link, Skeleton, Slide, Slider
} from '@components/ui';
import { CONFIG } from '@constants/config';
import { arraysEqual, isArrayEmpty } from '@helpers/ArrayHelpers';
import { useAnalytics } from '@hooks/useAnalytics';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { usePrevious } from '@hooks/usePrevious';
import { useUser } from '@hooks/data/useUser';

import ProductCard from '../ProductCard/ProductCard';
import ProductCardSkeleton from '../ProductCard/ProductCardSkeleton';

import styles from './ProductsCarousel.module.scss';

const ProductsCarousel = (props) => {

  const {
    className,
    headerClassName,
    sliderClassName,
    iconClassName,
    titleClassName,
    prevClassName,
    nextClassName,
    //
    icon,
    title,
    subtitle,
    link,
    image,
    //
    products,
    productListProps = {},
    productCardProps = {},
    //
    withViewMoreLink = true,
    withViewMoreCard = true,
    watchSlidesProgress = true,
    spaceBetween,
    breakpoints = DEFAULT_BREAKPOINTS,
    //
    loading,
    loadingCount,
    withLoadingHeader,
    //
    onItemClick = () => {},
    onItemQuantityChange = () => {},
    onLinkClick,
    //
    cardComponent: CardComponent = ProductCard
  } = props;

  // Props

  const {
    src: imageSrc,
    url: imageUrl
  } = image || {};

  // Refs

  const [swiperRef, setSwiperRef] = React.useState(null);

  // Hooks

  const analytics = useAnalytics();
  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  // Data Hooks

  const { data: user } = useUser({ isOptional: true });

  const previousProducts = usePrevious(products);

  // Analytics

  const { observe } = useInView({
    threshold: 0.75,
    onEnter: ({ unobserve }) => {
      analytics.viewItemList(products, {
        id: productListProps?.analyticsListId,
        name: productListProps?.analyticsListName,
      });
      unobserve();
    }
  });

  // Effects

  React.useEffect(() => {
    // Reset carousel when products are updated
    try {
      if (
        swiperRef
        && !isArrayEmpty(previousProducts)
        && !isArrayEmpty(products)
        && !arraysEqual(previousProducts, products)
      ) {
        swiperRef.slideTo(0, 0);
      }
    } catch (err) {
      console.log('Error while trying to reset carousel ', err);
    }
  }, [swiperRef, previousProducts, products]);

  // Handlers

  const onProductEnter = (product) => {
    analytics.viewItem(product, {
      id: productListProps?.analyticsListId,
      name: productListProps?.analyticsListName,
    });
  };

  // Loading

  const displayProducts = loading
    ? new Array(loadingCount || 12).fill({})
    : products;

  // Props

  const canViewMore = link?.url && link?.title;
  const isImageVisible = !!image;

  // Empty state

  if (isArrayEmpty(displayProducts, true)) {
    return null;
  }

  // Render

  return (
    <div ref={loading ? undefined : observe} className={classnames(styles.root, className)}>

      {/* Header */}
      <div
        className={classnames(
          styles.header,
          {
            [styles.withSubtitle]: subtitle,
            [styles.empty]: loading ? !withLoadingHeader : (!title && !link)
          },
          headerClassName
        )}
      >

        {/* Icon */}
        {
          icon && (
            <div className={classnames(styles.icon, iconClassName)}>
              <Image
                src={icon}
                width={20}
                height={20}
                quality="100"
                alt={title}
              />
            </div>
          )
        }

        {/* Title / Subtitle */}
        <div className={styles.titleContainer}>

          {/* Title */}
          <div className={classnames(styles.title, titleClassName)}>
            {
              loading
                ? (withLoadingHeader ? <Skeleton containerClassName={styles.skeleton} /> : null)
                : title
            }
          </div>

          {/* Subtitle */}
          {
            subtitle && (
              <div className={styles.subtitle}>
                {subtitle}
              </div>
            )
          }
        </div>

        {/* View More Link */}
        {
          (withViewMoreLink && canViewMore) && (
            <ViewMoreLink
              name={title}
              link={link}
              onLinkClick={onLinkClick}
            />
          )
        }
      </div>

      {/* Subtitle - mobile */}
      {subtitle && <div className={styles.subtitleMobile}>{subtitle}</div>}

      {/* Slider */}
      <Slider
        sliderClassName={classnames(styles.slider, sliderClassName)}
        prevClassName={classnames(styles.navButton, prevClassName)}
        nextClassName={classnames(styles.navButton, nextClassName)}
        spaceBetween={spaceBetween || (isBreakpoint ? 8 : 14)}
        slidesPerView={2}
        slidesPerGroup={2}
        breakpoints={breakpoints}
        navigation
        allowTouchMove={isMobile}
        preventClicks={isMobile}
        preventClicksPropagation={isMobile}
        noSwiping={!isMobile}
        watchSlidesProgress={watchSlidesProgress}
        onSwiper={setSwiperRef}
      >

        {/* Banner */}
        {
          isImageVisible && (
            <Slide className={styles.imageWrapper}>
              <Link passHref href={imageUrl} prefetch={CONFIG.PREFETCH_LINKS}>
                <a className={styles.image}>
                  <Image
                    src={imageSrc}
                    height={540}
                    width={320}
                    alt={title}
                    style={{
                      objectFit: 'cover'
                    }}
                  />
                </a>
              </Link>
            </Slide>
          )
        }

        {/* Product Cards */}
        {
          displayProducts?.map((product, index) => (
            <Slide key={loading ? `loading-${index}` : `${product.slug}${index}`} className={styles.item}>
              {
                loading
                  ? <ProductCardSkeleton />
                  : (
                    <CardComponent
                      className={styles.card}
                      product={product}
                      listProps={productListProps}
                      shouldShowGeniusPrice={user?.shouldShowGeniusPrice}
                      {...productCardProps}
                      onQuantityChange={onItemQuantityChange}
                      onEnter={() => {
                        onProductEnter(product);
                      }}
                      onClick={() => {
                        if (onItemClick) {
                          onItemClick();
                        }
                        analytics.selectItem(
                          product,
                          {
                            id: productListProps?.analyticsListId,
                            name: productListProps?.analyticsListName || title,
                            index
                          }
                        );
                      }}
                    />
                  )
              }
            </Slide>
          ))
        }

        {/* View More Card */}
        {
          (withViewMoreCard && canViewMore) && (
            <Slide className={styles.viewMoreItem}>
              <ViewMoreLink
                name={title}
                link={link}
                asCard
                onLinkClick={onLinkClick}
              />
            </Slide>
          )
        }
      </Slider>

    </div>
  );
};

// View More Link

const ViewMoreLink = (props) => {
  const {
    name,
    link,
    asCard,
    onLinkClick
  } = props;

  const {
    url, title
  } = link || {};

  const analytics = useAnalytics();

  if (!url || !title) return null;

  const classes = classnames(
    styles.viewMore,
    { [styles.viewMoreCard]: asCard },
    { [styles.viewMoreLink]: !asCard }
  );

  return (
    <Link
      href={link.url}
      as={link.as}
      prefetch={false}
      passHref
    >
      <Button
        tag="a"
        className={classes}
        contentClassName={styles.viewMoreContent}
        onClick={() => {
          analytics.selectProductsCarousel(name);
          if (onLinkClick) {
            onLinkClick();
          }
        }}
      >
        {
          !asCard
            ? (
              <>
                <span className={styles.viewMoreText}>{link.title}</span>
                <Icon
                  className={styles.viewMoreIcon}
                  name="arrow-narrow-right"
                  size={18}
                  strokeWidth={2}
                />
              </>
            ) : (
              <>
                <div className={styles.viewMoreIcon}>
                  <Icon
                    className={styles.icon}
                    name="arrow-narrow-right"
                    size={24}
                    strokeWidth={2}
                  />
                </div>
                <div className={styles.viewMoreText}>{link?.title}</div>
              </>
            )
        }
      </Button>
    </Link>
  );
};

// Breakpoints

const DEFAULT_BREAKPOINTS = {
  1600: {
    slidesPerView: 4
  },
  1440: {
    slidesPerView: 3
  },
  960: {
    slidesPerView: 4
  },
  640: {
    slidesPerView: 3
  }
};

ProductsCarousel.propTypes = {
  //
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  headerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  sliderClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  iconClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  titleClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  prevClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  nextClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  //
  icon: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.string,
  link: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }),
  onLinkClick: PropTypes.func,
  //
  withViewMoreLink: PropTypes.bool,
  withViewMoreCard: PropTypes.bool,
  spaceBetween: PropTypes.number,
  //
  products: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string
  })).isRequired,
  productListProps: PropTypes.shape({}),
  productCardProps: PropTypes.shape({}),
  cardComponent: PropTypes.elementType,
  image: PropTypes.shape({ size: PropTypes.number, src: PropTypes.shape({}), url: PropTypes.string }),
  //
  breakpoints: PropTypes.shape({}),
  loading: PropTypes.bool,
  loadingCount: PropTypes.number,
  withLoadingHeader: PropTypes.bool,
  //
  onItemClick: PropTypes.func,
  onItemQuantityChange: PropTypes.func,
};

export default ProductsCarousel;
